import { FormField, type OnChange } from "@common/controls/Form/component";
import { TextInput } from "@common/controls/Inputs/Text";
import { useI18n } from "@common/hooks/i18n";
import { usePromise } from "@common/lib/hooks";
import { type Account } from "@common/modules/account/types";
import * as React from "react";
import { type IRecipientFields, type LookupAccount } from "../types";
import { type AddUpdateRecipientFormData } from "./AddUpdateModal";
import WalletRecipientPreview from "./Preview/Wallet";

type Props = {
  ref: React.RefObject<IRecipientFields | null>;
  lookupAccount: LookupAccount;
  onChange: OnChange<AddUpdateRecipientFormData>;
  previewStyle?: React.CSSProperties;
  values: AddUpdateRecipientFormData;
};

export default (props: Props) => {
  const i18n = useI18n();

  const [account, isLoadingAccount, _, __, promise] = usePromise({
    fn: props.lookupAccount,
    args: [props.values.accountNumber],
    shouldLoad: !!props.values.accountNumber && props.values.accountNumber.toString().length >= 10,
    delay: 1000
  });

  React.useImperativeHandle(
    props.ref,
    () => ({
      getValues: async (data: AddUpdateRecipientFormData): Promise<Account.View.BeneficiarySettings> => {
        const account = await promise;
        if (!account) throw new Error("BUG");
        return {
          data: {
            wallet: {
              accountNumber: data.accountNumber!
            }
          },
          currencyCode: account.currencyCode,
          name: account.accountHolder
        };
      }
    }),
    [promise, props.values]
  );

  return (
    <>
      <FormField
        Component={TextInput}
        componentProps={{ isBusy: isLoadingAccount, maxLength: 19, type: "number" }}
        isRequired
        label={i18n.accountNumber}
        name="accountNumber"
        value={props.values.accountNumber}
        qa="Account number"
        validator={async () => {
          if (!promise) return i18n.errorThisAccountDoesNotExist;
          const account = await promise;
          return account ? null : i18n.errorThisAccountDoesNotExist;
        }}
      />

      {!!account && <WalletRecipientPreview account={account} style={props.previewStyle} />}
    </>
  );
};
